import React from 'react';
import clsx from 'clsx';
import Button from '@material-ui/core/Button';
import { fade, makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import PeopleIcon from '@material-ui/icons/People';

const SKELETON_ROWS = [
	[148, 88, 76, 60, 132, 140],
	[120, 72, 64, 60, 132, 120],
	[164, 96, 76, 60, 132, 148],
	[132, 80, 64, 60, 132, 128],
	[156, 88, 76, 60, 132, 136],
	[112, 72, 64, 60, 132, 120],
	[140, 96, 76, 60, 132, 144],
	[128, 80, 64, 60, 132, 124]
];

const useStyles = makeStyles((theme) => {
	const isDark = theme.palette.type === 'dark';
	const line = fade(theme.palette.text.primary, 0.12);
	const line2 = fade(theme.palette.text.primary, isDark ? 0.08 : 0.07);
	return {
		header: {
			display: 'flex',
			alignItems: 'center',
			flexWrap: 'wrap',
			gap: 16,
			padding: theme.spacing(1.5, 2),
			marginBottom: 16,
			borderRadius: '0 0 16px 16px',
			border: `1px solid ${theme.palette.divider}`,
			borderTop: 0
		},
		quotaRow: {
			display: 'flex',
			alignItems: 'center',
			gap: 16,
			padding: theme.spacing(1.25, 2),
			borderRadius: '16px 16px 0 0',
			border: `1px solid ${theme.palette.divider}`,
			borderBottom: `1px solid ${line}`
		},
		heading: {
			display: 'flex',
			alignItems: 'center',
			gap: 12,
			flex: 'none'
		},
		headingText: {
			display: 'flex',
			flexDirection: 'column',
			gap: 8
		},
		tile: {
			borderRadius: 12
		},
		segment: {
			display: 'flex',
			gap: 0
		},
		segmentOption: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			gap: 6,
			padding: theme.spacing(0.75, 1.5),
			'&:not(:last-child)': {
				borderRight: `1px solid ${theme.palette.divider}`
			}
		},
		grow: {
			flex: 1
		},
		bar: {
			borderRadius: 6
		},
		pill: {
			borderRadius: 11
		},
		button: {
			borderRadius: 10
		},
		card: {
			borderRadius: 16,
			border: `1px solid ${line2}`,
			boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.04)',
			backgroundColor: theme.palette.background.paper,
			overflow: 'hidden'
		},
		head: {
			display: 'flex',
			alignItems: 'center',
			gap: 16,
			height: 44,
			padding: '0 16px',
			boxSizing: 'border-box',
			backgroundColor: fade(theme.palette.text.primary, isDark ? 0.03 : 0.02),
			borderBottom: `1px solid ${line}`
		},
		row: {
			display: 'flex',
			alignItems: 'center',
			gap: 16,
			height: 56,
			padding: '0 16px',
			boxSizing: 'border-box',
			borderBottom: `1px solid ${line2}`,
			'&:last-child': { borderBottom: 0 }
		},
		col: {
			display: 'flex',
			alignItems: 'center',
			gap: 10,
			flex: 'none'
		},
		colIndex: { width: 28 },
		colUser: { width: 240 },
		colRole: { width: 104 },
		colStatus: { width: 96 },
		colDate: { width: 160 },
		colUpdated: { flex: 1, minWidth: 0 },
		colActions: { width: 96, justifyContent: 'flex-end' },
		empty: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			gap: 8,
			padding: '40px 24px 48px'
		},
		emptyIcon: {
			width: 48,
			height: 48,
			borderRadius: 14,
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: fade(theme.palette.text.primary, 0.05),
			color: theme.palette.text.disabled,
			'& svg': { fontSize: 26 }
		},
		emptyTitle: {
			display: 'block',
			marginTop: 6,
			fontSize: 15,
			fontWeight: 600,
			lineHeight: 1.4,
			color: theme.palette.text.primary
		},
		emptyButton: {
			marginTop: 10,
			borderRadius: 8,
			textTransform: 'none',
			fontWeight: 600
		}
	};
});

const SKELETON_COLUMNS = [
	{ name: 'index', widthClass: 'colIndex', headWidth: 12 },
	{ name: 'user', widthClass: 'colUser', headWidth: 44 },
	{ name: 'role', widthClass: 'colRole', headWidth: 36 },
	{ name: 'status', widthClass: 'colStatus', headWidth: 44 },
	{ name: 'created', widthClass: 'colDate', headWidth: 80 },
	{ name: 'updated', widthClass: 'colUpdated', headWidth: 80 },
	{ name: 'actions', widthClass: 'colActions', headWidth: 0 }
];

export function UserListSkeleton() {
	const classes = useStyles();
	return (
		<div role="status" aria-busy="true">
			<div className={classes.quotaRow}>
				<Skeleton animation="wave" variant="rect" width={160} height={10} className={classes.bar} />
				<Skeleton animation="wave" variant="rect" width={56} height={14} className={classes.bar} />
				<Skeleton animation="wave" variant="rect" height={8} className={clsx(classes.bar, classes.grow)} />
				<Skeleton animation="wave" variant="rect" width={80} height={12} className={classes.bar} />
			</div>
			<div className={classes.header}>
				<div className={classes.heading}>
					<Skeleton animation="wave" variant="rect" width={44} height={44} className={classes.tile} />
					<div className={classes.headingText}>
						<Skeleton animation="wave" variant="rect" width={150} height={16} className={classes.bar} />
						<Skeleton animation="wave" variant="rect" width={100} height={10} className={classes.bar} />
					</div>
				</div>
				<div className={classes.segment}>
					{[0, 1, 2].map((i) => (
						<div key={i} className={classes.segmentOption}>
							<Skeleton animation="wave" variant="rect" width={28} height={16} className={classes.bar} />
							<Skeleton animation="wave" variant="rect" width={44} height={8} className={classes.bar} />
						</div>
					))}
				</div>
				<Skeleton animation="wave" variant="rect" width={300} height={40} className={classes.button} />
				<span className={classes.grow} />
				<Skeleton animation="wave" variant="rect" width={40} height={40} className={classes.button} />
				<Skeleton animation="wave" variant="rect" width={40} height={40} className={classes.button} />
			</div>
			<div className={classes.card}>
				<div className={classes.head}>
					{SKELETON_COLUMNS.map((col) => (
						<div key={col.name} className={`${classes.col} ${classes[col.widthClass]}`}>
							{col.headWidth > 0 && (
								<Skeleton animation="wave" variant="rect" width={col.headWidth} height={10} className={classes.bar} />
							)}
						</div>
					))}
				</div>
				{SKELETON_ROWS.map((widths, idx) => (
					<div key={idx} className={classes.row}>
						<div className={`${classes.col} ${classes.colIndex}`}>
							<Skeleton animation="wave" variant="rect" width={16} height={12} className={classes.bar} />
						</div>
						<div className={`${classes.col} ${classes.colUser}`}>
							<Skeleton animation="wave" variant="circle" width={32} height={32} />
							<div className={classes.headingText}>
								<Skeleton animation="wave" variant="rect" width={widths[0]} height={12} className={classes.bar} />
								<Skeleton animation="wave" variant="rect" width={widths[1]} height={10} className={classes.bar} />
							</div>
						</div>
						<div className={`${classes.col} ${classes.colRole}`}>
							<Skeleton animation="wave" variant="rect" width={widths[2]} height={22} className={classes.pill} />
						</div>
						<div className={`${classes.col} ${classes.colStatus}`}>
							<Skeleton animation="wave" variant="rect" width={widths[3]} height={22} className={classes.pill} />
						</div>
						<div className={`${classes.col} ${classes.colDate}`}>
							<Skeleton animation="wave" variant="rect" width={widths[4]} height={12} className={classes.bar} />
						</div>
						<div className={`${classes.col} ${classes.colUpdated}`}>
							<Skeleton animation="wave" variant="rect" width={widths[5]} height={12} className={classes.bar} />
						</div>
						<div className={`${classes.col} ${classes.colActions}`}>
							<Skeleton animation="wave" variant="rect" width={32} height={32} className={classes.button} />
							<Skeleton animation="wave" variant="rect" width={40} height={20} className={classes.pill} />
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

export function UserListEmpty({ language, showClear, onClear }) {
	const classes = useStyles();
	return (
		<div className={classes.empty}>
			<span className={classes.emptyIcon}><PeopleIcon /></span>
			<span className={classes.emptyTitle}>{language.no_data}</span>
			{showClear && (
				<Button variant="outlined" size="small" className={classes.emptyButton} onClick={onClear}>
					{language.clear}
				</Button>
			)}
		</div>
	);
}
